<template>
  <div :class="$style.grid_editor_container">
    <grid-input
      type="text"
      :value="value"
      text-align="start"
      :initial-select="false"
      @onInput="handleInput"
      @keydown="onKeyDown"
    />

    <div
      v-if="tasksForShow.length"
      ref="popup"
      :style="{
        top: coordinates.top,
        left: coordinates.left,
        'max-height': '180px',
      }"
      :class="[$style.grid_editor_popup, dropToBottom ? '' : $style.popup_top, 'gantt_scroll']"
    >
      <div
        v-for="(option, key) in tasksForShow"
        :key="key"
        :class="[$style.popup_item, option.id === focusedId ? $style.popup_item_focused : '']"
        @click.stop="selectItem(option)"
        v-html="option.template"
      />
    </div>
  </div>
</template>

<script>
import GridInput from '../components/input.vue';

export default {
  name: 'GridPredecessorsCellEditor',
  components: { GridInput },
  props: {
    placeholder: {
      type: Object,
      required: true,
    },
    tasksForSearch: {
      type: Array,
      required: true,
    },
    onSave: {
      type: Function,
      required: true,
    },
    onCancel: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      value: null,
      locales: __,
      coordinates: {
        left: 'auto',
        top: 'auto',
      },
      dropToBottom: true,
      gridLinkColumn: {},
      tasksForShow: [],
      focusedId: null,
    };
  },
  watch: {
    tasksForShow(tasks) {
      if (tasks.length) {
        this.$nextTick(() => {
          this.calcCoordinates();
        });
      }

      this.focusedId = null;
    },
  },
  mounted() {
    this.placeholder.addEventListener('wheel', this.onScrollPlaceholder, { passive: true });
  },
  beforeUnmount() {
    this.placeholder.removeEventListener('wheel', this.onScrollPlaceholder);
  },
  methods: {
    handleInput(value, selectionStart) {
      this.value = value;
      this.filteredList(value, selectionStart);
    },

    calcCoordinates() {
      const coordinates = this.placeholder.getBoundingClientRect();
      const clientHeight = document.documentElement.clientHeight;
      const elementHeight = this.$refs.popup.offsetHeight;
      // const appFooter = 0;

      this.dropToBottom = (coordinates.bottom + elementHeight) < clientHeight;

      if (this.dropToBottom) {
        this.coordinates = {
          top: `${coordinates.bottom + 1}px`,
          left: `${coordinates.left}px`,
        };
      } else {
        this.coordinates = {
          top: `${coordinates.top - elementHeight - 1}px`,
          left: `${coordinates.left}px`,
        };
      }
    },

    filteredList(newVal, selectionPos) {
      this.tasksForShow = [];

      const stringBeforeSelection = newVal.slice(0, selectionPos);
      let editElemBegin = stringBeforeSelection.lastIndexOf(',');

      editElemBegin = editElemBegin !== -1 ? editElemBegin + 1 : 0;
      let editElemEnd = newVal.indexOf(',', selectionPos);

      editElemEnd = editElemEnd !== -1 ? editElemEnd : newVal.length;
      const value = newVal.slice(editElemBegin, editElemEnd).trim();

      if (!value) return;

      const selectedWbs = this.getSelectedWbsOptions(newVal, editElemBegin, editElemEnd);
      const tasksForSearch = this.tasksForSearch.filter(task => !selectedWbs.includes(task.wbs));

      // find tasks include search string either in name or wbs
      const filteredTasks = tasksForSearch.filter(task => task.value.toLowerCase().includes(value.toLowerCase()) || task.wbs.includes(value)).sort((task1, task2) => {
        if (task2.wbs.startsWith(value)) {
          return 1;
        } if (task1.wbs.startsWith(value)) {
          return -1;
        }

        return 0;
      });

      filteredTasks.forEach(item => {
        const regex = new RegExp(value, 'gi');
        const template = `${item.wbs} ${item.value}`;
        const a = template.replace(regex, matchedText => `<span class="${this.$style.highlight} ${this.$style.task_item} ">${matchedText}</span>`);

        this.tasksForShow.push({ id: item.id, template: `<span class="${this.$style.task_item} ">${a}</span>`, wbs: item.wbs });
      });

      this.gridLinkColumn = {
        valueStartPos: editElemBegin,
        valueEndPos: editElemEnd,
      };
    },

    getSelectedWbsOptions(inputValue, editElemBegin, editElemEnd) {
      const valuesBefore = inputValue.slice(0, editElemBegin);
      const valuesAfter = inputValue.slice(editElemEnd);

      return [
        ...valuesBefore ? valuesBefore.split(',').map(wbs => wbs.trim()) : [],
        ...valuesAfter ? valuesAfter.split(',').map(wbs => wbs.trim()) : [],
      ];
    },

    selectItem(item) {
      const wbs = item.wbs;
      const prevValue = this.value;
      const newValue = `${prevValue.slice(0, this.gridLinkColumn.valueStartPos)}${wbs}${prevValue.slice(this.gridLinkColumn.valueEndPos)}`;
      let resultValue = newValue.split(',').map(val => val.trim()).join(', ');

      if (this.gridLinkColumn.valueEndPos === prevValue.length) {
        resultValue = `${resultValue},`;
      }

      this.value = resultValue;
      this.tasksForShow = [];
    },

    onScrollPlaceholder(event) {
      // stop event bubbling to allow scrolling inside editor
      event.stopPropagation();
    },

    moveHandler(isSelect, num = 0) {
      if (!this.tasksForShow.length) {
        return;
      }

      if (isSelect) {
        const focusedTask = this.tasksForShow.find(task => task.id === this.focusedId);

        focusedTask && this.selectItem(focusedTask);

        return;
      }

      let pos = 0;

      this.tasksForShow.forEach((task, index) => {
        if (task.id === this.focusedId) {
          pos = index;
        }
      });

      pos += num;

      if (pos < 0) {
        pos = 0;
      }

      if (pos > this.tasksForShow.length - 1) {
        pos = this.tasksForShow.length - 1;
      }

      if (this.tasksForShow[pos]?.id) {
        this.focusedId = this.tasksForShow[pos].id;
      }
    },

    onKeyDown(event) {
      if (event.keyCode === 38) { // up
        this.moveHandler(false, -1);
        event.preventDefault();
      }

      if (event.keyCode === 40) { // down
        this.moveHandler(false, 1);
        event.preventDefault();
      }

      if (event.keyCode === 13) { // enter
        if (this.tasksForShow.length) {
          this.moveHandler(true);
        } else {
          this.onSave();
        }
      }

      if (event.keyCode === 27) { // esc
        this.onCancel();
      }
    },
  },
};
</script>

<style module src="../styles/gridEditor.less" lang="less"></style>
